import { useCallback } from 'react';
import useFormManager from '../../common/hooks/useFormManager';
import useUpdateEffect from '../../common/hooks/useUpdateEffect';
import useValidatedField from '../../common/hooks/useValidatedField';

export default function useResetPasswordFormManager(
    reset: (password: string) => void,
    resettingInProcess: boolean,
    resettingComplete: boolean | null,
    serverErrorMessages: { password: string | undefined },
) {
    const { showValidationErrors, setShowValidationErrors, notNullValidator } =
        useFormManager();
    
    const {
        value: password,
        setValue: setPassword,
        validated: passwordValidated,
        setValidated: setPasswordValidated,
        errorState: passwordErrorMessage
    } = useValidatedField(notNullValidator);

    const confirmPasswordValidator = useCallback(
        (text: string) => {
            if (text.length === 0) return 'Missing required entry';
            if (text !== password) return 'Passwords do not match.';
            return null;
        },
        [password]
    );
    
    const {
        value: confirmPassword,
        setValue: setConfirmPassword,
        validated: confirmPasswordValidated,
        setValidated: setConfirmPasswordValidated,
        errorState: confirmPasswordErrorMessage
    } = useValidatedField(confirmPasswordValidator);

    const resetFormSuccessfulOrNotSubmitted =
        resettingComplete === null ? true : resettingComplete;

    const onResetPasswordStateChange = () => {
        if (serverErrorMessages.password) {
            setPasswordValidated(serverErrorMessages.password === undefined);
        } else {
            setPasswordValidated(resetFormSuccessfulOrNotSubmitted);
            setConfirmPasswordValidated(resetFormSuccessfulOrNotSubmitted);
        }
    };

    useUpdateEffect(onResetPasswordStateChange, [
        resetFormSuccessfulOrNotSubmitted,
        setPasswordValidated,
        setConfirmPasswordValidated,
        serverErrorMessages,
    ]);

    const isInProcess = resettingInProcess;

    const resetPasswordWithForm = () => {
        const formFieldsValid = passwordValidated && confirmPasswordValidated;
        const canResetPassword = formFieldsValid && !isInProcess;

        setShowValidationErrors(true);

        if (canResetPassword) {
            reset(password);
        }
    };

    return {
        password,
        setPassword,
        passwordValidated,
        passwordErrorMessage,
        confirmPassword,
        setConfirmPassword,
        confirmPasswordValidated,
        confirmPasswordErrorMessage,
        resetPasswordWithForm,
        showValidationErrors,
        isInProcess,
        resetFormSuccessfulOrNotSubmitted,
    };
}
